import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Edit2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import type { Patient } from '../../../../shared/professional-types';

interface UpdatePatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient | null;
  onPatientUpdated: () => void;
}

export default function UpdatePatientModal({
  isOpen,
  onClose,
  patient,
  onPatientUpdated,
}: UpdatePatientModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    identificador: '',
    diagnostico: '',
    notas_iniciales: '',
  });

  const patientLabel = (p: Patient): string => {
    if (p.identificador) return p.identificador;
    if (p.first_name || p.last_name)
      return `${p.first_name || ''} ${p.last_name || ''}`.trim();
    return p.username || `Paciente #${p.id}`;
  };

  // Cargar datos del paciente cuando se abre el modal
  useEffect(() => {
    if (patient) {
      setFormData({
        identificador: patient.identificador || '',
        diagnostico: patient.diagnostico || '',
        notas_iniciales: patient.notas_iniciales || '',
      });
      setError('');
      setSuccess(false);
    }
  }, [patient]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patient) return;

    setError('');
    setSuccess(false);

    try {
      setLoading(true);
      const response = await api.put(`/professional/patients/${patient.id}`, formData);

      if (response.data.ok) {
        setSuccess(true);
        onPatientUpdated();
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al actualizar paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ identificador: '', diagnostico: '', notas_iniciales: '' });
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!patient) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="glass-container max-w-2xl w-full pointer-events-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Edit2 className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-display font-bold">
                      Editar Paciente
                    </h2>
                    <p className="text-sm text-white/60">
                      {patientLabel(patient)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Formulario */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Error */}
                {error && (
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                )}

                {/* Success */}
                {success && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-green-300 text-sm">
                      Paciente actualizado correctamente
                    </p>
                  </div>
                )}

                {/* Info del paciente */}
                <div className="p-4 rounded-lg bg-white/5 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-300 font-semibold text-lg">
                        {patientLabel(patient).slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold">{patientLabel(patient)}</div>
                    </div>
                  </div>
                </div>

                {/* Identificador */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    ID de paciente
                  </label>
                  <input
                    type="text"
                    value={formData.identificador}
                    onChange={(e) =>
                      setFormData({ ...formData, identificador: e.target.value })
                    }
                    className="input-neureka"
                    placeholder="Código o número asignado por el centro"
                  />
                </div>

                {/* Diagnóstico */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    Diagnóstico
                  </label>
                  <input
                    type="text"
                    value={formData.diagnostico}
                    onChange={(e) =>
                      setFormData({ ...formData, diagnostico: e.target.value })
                    }
                    className="input-neureka"
                    placeholder="Ej: Deterioro cognitivo leve"
                  />
                </div>

                {/* Notas Iniciales */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    Notas
                  </label>
                  <textarea
                    value={formData.notas_iniciales}
                    onChange={(e) =>
                      setFormData({ ...formData, notas_iniciales: e.target.value })
                    }
                    className="input-neureka resize-none"
                    rows={6}
                    placeholder="Observaciones y notas del paciente..."
                  />
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn-neureka-secondary"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-neureka"
                    disabled={loading || success}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Guardando...
                      </>
                    ) : success ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Guardado
                      </>
                    ) : (
                      <>
                        <Edit2 className="w-4 h-4" />
                        Guardar Cambios
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
